import styles from "./AdditionalAmenitiesModal.module.scss";
import { PhotoProvider, PhotoView } from "react-photo-view";
import Select, { MultiValue } from "react-select";
import { Switch } from "react-animated-switch";
import Modal from "../../Common/Modal/Modal";
import { CiZoomIn } from "react-icons/ci";
import React from "react";

type ParkingSlot = {
  ParkingId: number;
  ParkingName: string;
};

type ParkingOption = {
  amenityId: number;
  name?: string;
  amenityName?: string;
  image?: string;
  rates?: number;
  cost?: number;
  parkingdata?: ParkingSlot[];
};

type SlotOption = {
  label: string;
  value: string;
};

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  parkingOptions?: ParkingOption[];
  parkingIds: number[];
  setParkingIds: React.Dispatch<React.SetStateAction<number[]>>;
  notifyParkingIds: number[];
  setNotifyParkingIds: React.Dispatch<React.SetStateAction<number[]>>;
}

const AdditionalAmenitiesModal = (props: Props) => {
  const {
    isOpen,
    setIsOpen,
    parkingOptions,
    parkingIds,
    setParkingIds,
    notifyParkingIds,
    setNotifyParkingIds,
  } = props;

  const getAmenitySlotIds = (opt: ParkingOption) =>
    (opt.parkingdata ?? []).map((slot) => slot.ParkingId);

  const getSelectedSlotIds = (opt: ParkingOption) => {
    const amenitySlotIds = getAmenitySlotIds(opt);
    return parkingIds.filter((id) => amenitySlotIds.includes(id));
  };

  const handleSlotsChange = (
    opt: ParkingOption,
    selected: MultiValue<SlotOption>,
  ) => {
    const amenitySlotIds = getAmenitySlotIds(opt);
    const nextSelectedIds = selected.map((option) => Number(option.value));

    setParkingIds((prev) => [
      ...prev.filter((id) => !amenitySlotIds.includes(id)),
      ...nextSelectedIds,
    ]);
  };

  const toggleNotify = (amenityId: number, checked: boolean) => {
    setNotifyParkingIds((prev) =>
      checked
        ? prev.includes(amenityId)
          ? prev
          : [...prev, amenityId]
        : prev.filter((id) => id !== amenityId),
    );
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} showCloseIcon={true}>
      <div className={styles.additionalAmenitiesModalContainer}>
        <div className={styles.headingContainer}>
          <p className={styles.heading}>Select Additional Amenities</p>
        </div>
        <div className={styles.parkingGroupContainer}>
          {parkingOptions?.map((opt) => {
            const parkingSlots = opt.parkingdata ?? [];
            const noneAvailable = parkingSlots.length === 0;
            const selectedSlotIds = getSelectedSlotIds(opt);
            const rate = opt.rates ?? opt.cost ?? 0;
            const price = rate * selectedSlotIds.length;
            const displayName = opt.name ?? opt.amenityName ?? "Parking";

            const slotOptions: SlotOption[] = parkingSlots.map((slot) => ({
              label: slot.ParkingName,
              value: String(slot.ParkingId),
            }));

            const selectedOptions = slotOptions.filter((option) =>
              selectedSlotIds.includes(Number(option.value)),
            );

            return (
              <div key={opt.amenityId} className={styles.parkingGroup}>
                {noneAvailable && (
                  <div className={styles.notAvailableContainer}>
                    <p className={styles.label}>Unavailable</p>
                  </div>
                )}
                {displayName && (
                  <p className={styles.parkingGroupHeading}>{displayName}</p>
                )}

                <div className={styles.parkingContainer}>
                  <div className={styles.contentContainer}>
                    <div className={styles.contentInnerContainer}>
                      {!noneAvailable ? (
                        <div className={styles.labelContainer}>
                          <Select
                            isMulti
                            className={styles.input}
                            options={slotOptions}
                            value={selectedOptions}
                            placeholder="Select parking slots"
                            onChange={(selected) =>
                              handleSlotsChange(opt, selected)
                            }
                          />
                        </div>
                      ) : (
                        <div className={styles.labelContainer}>
                          <p className={styles.label}>
                            No parking slots available
                          </p>
                        </div>
                      )}

                      <div className={styles.priceContainer}>
                        <p>$ {price}</p>
                      </div>

                      {opt.image && (
                        <div className={styles.imageContainer}>
                          <PhotoProvider>
                            <PhotoView key={opt.image} src={opt.image}>
                              <div className={styles.imageTrigger}>
                                <img
                                  src={opt.image}
                                  className={styles.image}
                                  alt={displayName}
                                />
                                <div className={styles.imageOverlay}>
                                  <CiZoomIn className={styles.icon} />
                                </div>
                              </div>
                            </PhotoView>
                          </PhotoProvider>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {noneAvailable && (
                  <div className={styles.noticeContainer}>
                    <div className={styles.switchContainer}>
                      <Switch
                        inActiveTrackColor="#DFDFDF"
                        activeTrackColor="#C0F496"
                        inActiveThumbColor="#000000"
                        activeThumbColor="#000000"
                        checked={notifyParkingIds.includes(opt.amenityId)}
                        onChange={(val) => toggleNotify(opt.amenityId, val)}
                      />
                    </div>

                    <p className={styles.label}>
                      Notify me when parking is available
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.buttonContainer}>
          <button className={styles.button} onClick={() => setIsOpen(false)}>
            Continue
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdditionalAmenitiesModal;
